import axios, { AxiosError, AxiosRequestHeaders, AxiosResponse } from 'axios';
import { store } from '@/store';
import { mdiAlert as errorIcon } from '@mdi/js';
import common from '@/translations/fr/common';

// Extend AxiosRequestConfig to add custom properties
declare module 'axios' {
	export interface AxiosRequestConfig {
		skipInstanceNotification?: boolean;
	}
}

interface ErrorProblem {
	message?: string;
	[key: string]: string | number | undefined;
}

interface ErrorResponsePayload {
	message?: string;
	problems?: ErrorProblem[];
}

type HttpError = AxiosError<ErrorResponsePayload>;

import { RolesEnum } from '@/types/enum/RolesEnum';
import { HttpCodesEnum } from '@/types/enum/HttpCodesEnum';
import { NOTIFICATION_PATH } from '@/constants/uri';
import { router } from '@/router';

/** API_URL env var */
declare const QRP_BFF_URL: string;
declare const QRP_API_KEY: string;

const headers: AxiosRequestHeaders = {
	'Accept': 'application/json',
	'Content-Type': 'application/json'
};

headers['x-api-key'] = QRP_API_KEY;

/** The axios instance */
const instanceBff = axios.create({
	withCredentials: true,
	baseURL: QRP_BFF_URL,
	headers: headers
});

/** Default error message */
const DEFAULT_ERROR_MESSAGE = common.defaultErrorMessage;
const DEFAULT_OAUTH_MESSAGE = common.oauthErrorMessage;

/**
 * Gestionnaire centralisé des erreurs HTTP
 * Gère les erreurs de manière unifiée pour toutes les instances axios
 */
async function handleHttpError(error: HttpError): Promise<never> {
	const type = store.getters['auth/getUserType'] ?? RolesEnum.ASSURE;
	let errorMessage = '';

	// Erreur serveur (500+) ou pas de réponse
	if (!error.response || error.response.status >= HttpCodesEnum.HTTP_SERVER_ERROR) {
		errorMessage = DEFAULT_ERROR_MESSAGE;
	} else if (error.response.status === HttpCodesEnum.HTTP_UNAUTHORIZED_ERROR) {
		// Erreur 401 - Non autorisé
		errorMessage = await handle401Error(error);
	} else if (error.response.status === HttpCodesEnum.HTTP_FORBIDDEN_ERROR) {
		// Erreur 403 - Accès interdit
		errorMessage = await handle403Error(error, type);
	} else {
		// Autres erreurs - Extraction du message
		errorMessage = extractErrorMessage(error);
	}

	// Envoyer la notification d'erreur si un message existe
	if (!error.config?.skipInstanceNotification && errorMessage) {
		store.dispatch(NOTIFICATION_PATH, {
			type: 'error',
			message: errorMessage,
			icon: errorIcon
		});
	}

	return Promise.reject(error);
}

/**
 * Extrait le code/message d'erreur depuis la réponse
 * Gère les formats: { message: "..." } et { problems: [{ message: "..." }] }
 */
function getErrorMessage(error: HttpError): string | null {
	// Format avec tableau de problèmes
	if (error.response?.data?.problems && Array.isArray(error.response.data.problems) && error.response.data.problems.length > 0) {
		return error.response.data.problems[0].message || null;
	}
	// Format avec message simple
	return error.response?.data?.message || null;
}

/**
 * Gestion spécifique des erreurs 401
 */
async function handle401Error(error: HttpError): Promise<string> {
	const errorMessage: string | null = getErrorMessage(error);
	const hasUserInfo = store.getters['auth/hasUserInfo'];

	if (errorMessage?.startsWith('cgu.invalid')) {
		if (hasUserInfo && router.currentRoute.name !== 'cgu') {
			await router.push({ name: 'updateCgu' });
		}
	} else if (errorMessage) {
		error.response = undefined;

		if (store.getters['auth/hasUserInfo']) {
			await store.dispatch('auth/logout');
		}
	}

	return errorMessage ?? DEFAULT_OAUTH_MESSAGE;
}

/**
 * Gestion spécifique des erreurs 403
 */
async function handle403Error(error: HttpError, type: string): Promise<string> {
	const errorMessage = getErrorMessage(error);

	if (type === RolesEnum.TEMOIN && errorMessage === 'Access Denied.') {
		if (store.getters['auth/hasUserInfo']) {
			await store.dispatch('auth/logout');
		}
		return '';
	}

	return errorMessage || DEFAULT_ERROR_MESSAGE;
}

/**
 * Extraction du message d'erreur depuis la réponse
 * Gère différents formats de réponse (message simple, tableau de problèmes)
 */
function extractErrorMessage(error: AxiosError): string {
	// Format avec tableau de problèmes - on récupère tous les messages
	if (error.response?.data?.problems && Array.isArray(error.response.data.problems)) {
		return error.response.data.problems
			.map((problem: Record<string, string | number>) => problem.message)
			.filter(Boolean)
			.join(', ');
	}
	// Format avec message simple
	return error.response?.data?.message || DEFAULT_ERROR_MESSAGE;
}

/**
 * Intercepteur de requête pour ajouter les tokens d'authentification
 */
instanceBff.interceptors.request.use((config) => {
	const mceToken: string | null = sessionStorage.getItem('token_jwt');

	if (mceToken && config.headers) {
		config.headers['x-mce-token'] = mceToken;
		config.withCredentials = false;
		return config;
	}

	return config;
});

instanceBff.interceptors.response.use(undefined, handleHttpError);

export { instanceBff as axiosBff };
export { AxiosResponse };
